import { Fragment, useEffect, useMemo, useRef } from "react";
import { FieldColumnName } from "../../fields/FieldColumnName";
import { FieldFileType } from "../../fields/FieldFileType";
import { PanelProps } from "../ModalCreateColumn";
import { FILE_TYPES_BY_KEY } from "types/FileTypes";

interface PanelCreateColumnFileProps extends PanelProps {
  singleSelection: boolean;
}

export const PanelCreateColumnFile = ({
  autoFocus = true,
  isDefinitionEditable = true,
  fields,
  onFieldChange,
  singleSelection,
}: PanelCreateColumnFileProps) => {
  const nameFieldRef = useRef<HTMLInputElement>(null);

  // Parse file type from column definition
  // TODO: Update after file types are deprecated
  const fileTypes = useMemo(() => {
    return fields.definition.value?.kind === "file"
      ? fields.definition.value.file_types
      : [];
  }, [fields.definition.value]);

  /**
   * Shifts focus to the name field.
   *
   * This is tried multiple times because EuiSuperSelect (used by FieldFileType)
   * refocuses itself after onChange is called. I believe this is a bug in EUI
   * but I think this is the best option in the absence of a long-term fix.
   */
  const focusNameField = () => {
    const timer = setInterval(() => {
      if (document.activeElement !== nameFieldRef.current) {
        nameFieldRef.current?.focus();
      } else {
        clearInterval(timer);
      }
    });
  };

  // if definition is not set, default to "any" file type
  useEffect(() => {
    if (fields.definition.value === undefined) {
      onFieldChange("definition", {
        value: {
          kind: "file",
          file_types: [FILE_TYPES_BY_KEY["unknown"].id], // "Any" file type
          file_formats: [],
          multiple: true,
        },
        isValid: true,
      });
    }
  }, [fields.definition, onFieldChange]);

  return (
    <Fragment>
      {
        // phasing out file types - only show if file type is not already set to any/unknown
        !(fileTypes.length === 0 || fileTypes[0] === 1) && (
          <FieldFileType
            disabled={!isDefinitionEditable}
            singleSelection={singleSelection}
            value={
              fileTypes.length > 0
                ? fileTypes
                : [FILE_TYPES_BY_KEY["unknown"].id]
            } // default to "Any" if no file type selected
            onChange={(value) => {
              // Update state
              onFieldChange("definition", {
                value: {
                  kind: "file",
                  file_types: value.map((v) => v.id),
                  file_formats: [],
                  multiple: true,
                },
                isValid: true,
              });

              // Auto-fill name field if not previously edited
              if (value.length === 1 && !fields.name.isDirty) {
                onFieldChange("name", {
                  value: value[0].name,
                  isValid: true,
                  isDirty: false,
                });
              }

              // Shift focus to name field
              focusNameField();
            }}
          />
        )
      }

      <FieldColumnName
        autoFocus={autoFocus}
        ref={nameFieldRef}
        value={fields.name.value}
        onChange={(field) => {
          onFieldChange("name", {
            value: field.value,
            isValid: field.isValid,
            isDirty: true,
          });
        }}
      />
    </Fragment>
  );
};
